<script setup>
import { ref, computed, watch, onMounted, onActivated } from 'vue'; // GK-HUB: added onActivated for keep-alive fix
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import CampaignsAPI from 'dashboard/api/campaigns';
import { useCampaignWizard } from '../composables/useCampaignWizard';

const { t } = useI18n();
const { wizardState, autoDetectColumns } = useCampaignWizard();

const labels = useMapGetter('labels/getLabels');
const fileInputRef = ref(null);
const isParsing = ref(false);
const isDragging = ref(false);
const storedFile = ref(null);
const audienceList = computed(
  () => labels.value?.map(item => ({ value: item.id, label: item.title })) ?? []
);

const columnOptions = computed(() =>
  wizardState.contactHeaders.map(h => ({ value: h, label: h }))
);

const previewRows = computed(() => wizardState.uploadedContacts.slice(0, 5));

// GK-HUB: ERP segment computed properties
const segmentOptions = computed(() => {
  const opts = wizardState.n8nSegments.map(s => ({
    value: s.id,
    label: `/${s.id} — ${s.name}`,
  }));
  opts.push({
    value: '__custom__',
    label: t('CAMPAIGN.WHATSAPP.WIZARD.CUSTOM_COMMAND'),
  });
  return opts;
});

const isCustomCommand = computed(
  () => wizardState.selectedSegmentId === '__custom__'
);

const selectedSegment = computed(() =>
  isCustomCommand.value
    ? null
    : wizardState.n8nSegments.find(s => s.id === wizardState.selectedSegmentId)
);

const segmentPreviewRows = computed(() =>
  wizardState.segmentContacts.slice(0, 5)
);

const gridCols = computed(() =>
  wizardState.n8nConfigured ? 'grid-cols-3' : 'grid-cols-2'
);
// GK-HUB: end N8N segment computed

const selectMode = mode => {
  wizardState.audienceMode = mode;
};

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const handleFile = async file => {
  const ext = file.name.split('.').pop().toLowerCase();
  if (!['csv', 'xlsx'].includes(ext)) {
    useAlert(t('CAMPAIGN.WHATSAPP.WIZARD.UPLOAD_ERROR_FORMAT'));
    return;
  }

  storedFile.value = file;
  isParsing.value = true;

  try {
    // Step 1: Upload without phone_column to get headers + preview
    const response = await CampaignsAPI.parseSpreadsheet(file, null, null);
    const data = response.data;
    wizardState.contactHeaders = data.headers || [];

    // Auto-detect phone and name columns from headers
    // This sets wizardState.phoneColumn which triggers the watch → reparseFile()
    autoDetectColumns(data.headers || []);

    // If no phone column was auto-detected, stop parsing (user will select manually)
    if (!wizardState.phoneColumn) {
      isParsing.value = false;
    }
    // Otherwise, the watch on phoneColumn will trigger reparseFile() automatically
  } catch (error) {
    isParsing.value = false;
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.WIZARD.UPLOAD_ERROR_NO_PHONE')
    );
  }
};

const onFileSelect = event => {
  const file = event.target.files?.[0];
  if (file) handleFile(file);
};

const onDrop = event => {
  isDragging.value = false;
  const file = event.dataTransfer.files?.[0];
  if (file) handleFile(file);
};

const reparseFile = async () => {
  if (!storedFile.value || !wizardState.phoneColumn) return;
  isParsing.value = true;
  try {
    const response = await CampaignsAPI.parseSpreadsheet(
      storedFile.value,
      wizardState.phoneColumn,
      wizardState.nameColumn
    );
    const data = response.data;
    wizardState.uploadedContacts = data.contacts || [];
    wizardState.uploadWarnings = data.warnings || {};
  } catch (error) {
    useAlert(error?.response?.data?.error || error.message);
  } finally {
    isParsing.value = false;
  }
};

// Re-parse when user changes phone or name column
watch(
  () => [wizardState.phoneColumn, wizardState.nameColumn],
  ([newPhone], [oldPhone]) => {
    if (storedFile.value && newPhone && newPhone !== oldPhone) {
      reparseFile();
    }
  }
);

// GK-HUB: N8N segment methods
const fetchN8nConfig = async () => {
  try {
    const response = await CampaignsAPI.getN8nSegments();
    const data = response.data;
    wizardState.n8nConfigured = data.configured || false;
    wizardState.n8nSegments = data.segments || [];
  } catch {
    wizardState.n8nConfigured = false;
    wizardState.n8nSegments = [];
  }
};

const onSegmentSelect = segmentId => {
  wizardState.selectedSegmentId = segmentId;
  wizardState.segmentParameters = {};
  wizardState.segmentContacts = [];
  wizardState.segmentHeaders = [];
  wizardState.segmentError = null;

  // Pre-fill default values from segment parameters
  const seg = wizardState.n8nSegments.find(s => s.id === segmentId);
  if (seg?.parameters) {
    seg.parameters.forEach(p => {
      if (p.default !== undefined && p.default !== null) {
        wizardState.segmentParameters[p.name] = p.default;
      }
    });
  }
};

const querySegmentContacts = async () => {
  if (!wizardState.selectedSegmentId) return;
  if (isCustomCommand.value && !wizardState.customEndpoint.trim()) return;

  wizardState.isQueryingSegment = true;
  wizardState.segmentError = null;
  wizardState.segmentContacts = [];
  wizardState.segmentHeaders = [];

  try {
    const customEp = isCustomCommand.value
      ? wizardState.customEndpoint.trim()
      : null;
    const segId = isCustomCommand.value
      ? '__custom__'
      : wizardState.selectedSegmentId;
    const response = await CampaignsAPI.querySegment(
      segId,
      wizardState.segmentParameters,
      customEp
    );
    const data = response.data;
    wizardState.segmentContacts = data.contacts || [];
    wizardState.segmentHeaders = data.headers || [];
  } catch (error) {
    wizardState.segmentError =
      error?.response?.data?.error ||
      t('CAMPAIGN.WHATSAPP.WIZARD.SEGMENT_ERROR');
  } finally {
    wizardState.isQueryingSegment = false;
  }
};

onMounted(() => {
  fetchN8nConfig();
});

// GK-HUB: fix — re-fetch N8N config when component reactivates from keep-alive cache
onActivated(() => {
  fetchN8nConfig();
});
// GK-HUB: end N8N segment methods
</script>

<template>
  <div class="flex flex-col gap-6">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.STEP_AUDIENCE') }}
    </h3>

    <!-- Mode Cards -->
    <div class="grid gap-4" :class="gridCols">
      <button
        type="button"
        class="flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all cursor-pointer text-center"
        :class="
          wizardState.audienceMode === 'spreadsheet'
            ? 'border-n-teal-9 bg-n-teal-9/10'
            : 'border-n-weak bg-n-alpha-black2 hover:border-n-slate-7'
        "
        @click="selectMode('spreadsheet')"
      >
        <span class="i-lucide-file-spreadsheet text-2xl text-n-slate-11" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.MODE_SPREADSHEET') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.MODE_SPREADSHEET_DESC') }}
        </span>
      </button>

      <button
        type="button"
        class="flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all cursor-pointer text-center"
        :class="
          wizardState.audienceMode === 'labels'
            ? 'border-n-teal-9 bg-n-teal-9/10'
            : 'border-n-weak bg-n-alpha-black2 hover:border-n-slate-7'
        "
        @click="selectMode('labels')"
      >
        <span class="i-lucide-tags text-2xl text-n-slate-11" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.MODE_LABELS') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.MODE_LABELS_DESC') }}
        </span>
      </button>

      <!-- GK-HUB: N8N Segment card (only if configured) -->
      <button
        v-if="wizardState.n8nConfigured"
        type="button"
        class="flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all cursor-pointer text-center"
        :class="
          wizardState.audienceMode === 'slash_command'
            ? 'border-n-teal-9 bg-n-teal-9/10'
            : 'border-n-weak bg-n-alpha-black2 hover:border-n-slate-7'
        "
        @click="selectMode('slash_command')"
      >
        <span class="i-lucide-terminal text-2xl text-n-slate-11" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.MODE_ERP_SEGMENT') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.MODE_ERP_SEGMENT_DESC') }}
        </span>
      </button>
      <!-- GK-HUB: end N8N Segment card -->
    </div>

    <!-- Spreadsheet Mode -->
    <div
      v-if="wizardState.audienceMode === 'spreadsheet'"
      class="flex flex-col gap-4"
    >
      <!-- Upload Zone -->
      <div
        class="flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed rounded-xl cursor-pointer transition-all"
        :class="
          isDragging
            ? 'border-n-teal-9 bg-n-teal-9/10'
            : 'border-n-weak hover:border-n-slate-7'
        "
        @click="triggerFileInput"
        @dragover.prevent="isDragging = true"
        @dragleave="isDragging = false"
        @drop.prevent="onDrop"
      >
        <span class="i-lucide-upload text-xl text-n-slate-11" />
        <span class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.UPLOAD_TITLE') }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.UPLOAD_FORMATS') }}
        </span>
        <input
          ref="fileInputRef"
          type="file"
          accept=".csv,.xlsx"
          class="hidden"
          @change="onFileSelect"
        />
      </div>

      <!-- Loading -->
      <div v-if="isParsing" class="flex items-center justify-center py-4">
        <Spinner />
      </div>

      <!-- Column Selection -->
      <div
        v-if="wizardState.contactHeaders.length > 0 && !isParsing"
        class="flex flex-col gap-3"
      >
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.PHONE_COLUMN') }}
          </label>
          <ComboBox
            v-model="wizardState.phoneColumn"
            :options="columnOptions"
            :placeholder="t('CAMPAIGN.WHATSAPP.WIZARD.PHONE_COLUMN')"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.NAME_COLUMN') }}
          </label>
          <ComboBox
            v-model="wizardState.nameColumn"
            :options="columnOptions"
            :placeholder="t('CAMPAIGN.WHATSAPP.WIZARD.NAME_COLUMN')"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>
      </div>

      <!-- Preview Table -->
      <div
        v-if="previewRows.length > 0 && !isParsing"
        class="overflow-x-auto rounded-lg border border-n-weak"
      >
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-n-alpha-black2">
              <th
                v-for="header in wizardState.contactHeaders"
                :key="header"
                class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 whitespace-nowrap"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in previewRows"
              :key="idx"
              class="border-t border-n-weak"
            >
              <td
                v-for="header in wizardState.contactHeaders"
                :key="header"
                class="px-3 py-2 text-n-slate-12 whitespace-nowrap"
              >
                {{ row[header] || '-' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Warnings -->
      <div
        v-if="wizardState.uploadedContacts.length > 0 && !isParsing"
        class="flex flex-wrap items-center gap-3 text-sm"
      >
        <span class="text-n-teal-11 font-medium">
          {{
            t('CAMPAIGN.WHATSAPP.WIZARD.VALID_CONTACTS', {
              count: wizardState.uploadedContacts.length,
            })
          }}
        </span>
        <span
          v-if="wizardState.uploadWarnings.missing_phone > 0"
          class="text-n-amber-11"
        >
          {{
            t('CAMPAIGN.WHATSAPP.WIZARD.MISSING_PHONE', {
              count: wizardState.uploadWarnings.missing_phone,
            })
          }}
        </span>
        <span
          v-if="wizardState.uploadWarnings.invalid_phone > 0"
          class="text-n-amber-11"
        >
          {{
            t('CAMPAIGN.WHATSAPP.WIZARD.INVALID_PHONE', {
              count: wizardState.uploadWarnings.invalid_phone,
            })
          }}
        </span>
        <span
          v-if="wizardState.uploadWarnings.duplicates > 0"
          class="text-n-amber-11"
        >
          {{
            t('CAMPAIGN.WHATSAPP.WIZARD.DUPLICATES', {
              count: wizardState.uploadWarnings.duplicates,
            })
          }}
        </span>
      </div>
    </div>

    <!-- Labels Mode -->
    <div
      v-if="wizardState.audienceMode === 'labels'"
      class="flex flex-col gap-3"
    >
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="wizardState.selectedLabels"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- GK-HUB: N8N Segment Mode -->
    <div
      v-if="wizardState.audienceMode === 'slash_command'"
      class="flex flex-col gap-4"
    >
      <!-- Segment Selector -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.SELECT_SEGMENT') }}
        </label>
        <ComboBox
          :model-value="wizardState.selectedSegmentId"
          :options="segmentOptions"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.WIZARD.SELECT_SEGMENT_PLACEHOLDER')
          "
          class="[&>div>button]:bg-n-alpha-black2"
          @update:model-value="onSegmentSelect"
        />
      </div>

      <!-- Custom Command Input -->
      <div v-if="isCustomCommand" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.CUSTOM_COMMAND_LABEL') }}
        </label>
        <Input
          v-model="wizardState.customEndpoint"
          type="text"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.WIZARD.CUSTOM_COMMAND_PLACEHOLDER')
          "
        />
        <span class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.CUSTOM_COMMAND_HINT') }}
        </span>
      </div>

      <!-- Segment Description -->
      <p
        v-if="selectedSegment?.description"
        class="text-sm text-n-slate-11 -mt-2"
      >
        {{ selectedSegment.description }}
      </p>

      <!-- Dynamic Parameters -->
      <div
        v-if="selectedSegment?.parameters?.length > 0"
        class="flex flex-col gap-3"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.SEGMENT_PARAMS_TITLE') }}
        </span>
        <div
          v-for="param in selectedSegment.parameters"
          :key="param.name"
          class="flex flex-col gap-1"
        >
          <label class="text-xs text-n-slate-11">
            {{ param.label || param.name }}
            <span v-if="param.required" class="text-n-ruby-11">*</span>
          </label>
          <Input
            :model-value="wizardState.segmentParameters[param.name] ?? ''"
            :type="param.type === 'number' ? 'number' : 'text'"
            :placeholder="param.label || param.name"
            @update:model-value="
              val => (wizardState.segmentParameters[param.name] = val)
            "
          />
        </div>
      </div>

      <!-- Query Button -->
      <div
        v-if="
          wizardState.selectedSegmentId &&
          (!isCustomCommand || wizardState.customEndpoint.trim())
        "
        class="flex"
      >
        <Button
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.QUERY_SEGMENT')"
          icon="i-lucide-search"
          :is-loading="wizardState.isQueryingSegment"
          :disabled="wizardState.isQueryingSegment"
          @click="querySegmentContacts"
        />
      </div>

      <!-- Loading -->
      <div
        v-if="wizardState.isQueryingSegment"
        class="flex items-center justify-center py-4"
      >
        <Spinner />
      </div>

      <!-- Error -->
      <div
        v-if="wizardState.segmentError"
        class="p-3 rounded-lg bg-n-ruby-3 border border-n-ruby-7 text-sm text-n-ruby-11"
      >
        {{ wizardState.segmentError }}
      </div>

      <!-- Preview Table (reuses same pattern as spreadsheet) -->
      <div
        v-if="segmentPreviewRows.length > 0 && !wizardState.isQueryingSegment"
        class="overflow-x-auto rounded-lg border border-n-weak"
      >
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-n-alpha-black2">
              <th
                v-for="header in wizardState.segmentHeaders"
                :key="header"
                class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 whitespace-nowrap"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in segmentPreviewRows"
              :key="idx"
              class="border-t border-n-weak"
            >
              <td
                v-for="header in wizardState.segmentHeaders"
                :key="header"
                class="px-3 py-2 text-n-slate-12 whitespace-nowrap"
              >
                {{ row[header] || '-' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Contact Count -->
      <div
        v-if="
          wizardState.segmentContacts.length > 0 &&
          !wizardState.isQueryingSegment
        "
        class="flex items-center gap-3 text-sm"
      >
        <span class="text-n-teal-11 font-medium">
          {{
            t('CAMPAIGN.WHATSAPP.WIZARD.VALID_CONTACTS', {
              count: wizardState.segmentContacts.length,
            })
          }}
        </span>
      </div>
    </div>
    <!-- GK-HUB: end N8N Segment Mode -->
  </div>
</template>
